import { api } from '@packages/backend/convex/_generated/api';
import { elapsedTime, getUserErrorMessage } from '@packages/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { CheckCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ActionDropdown } from '@/components/ActionDropdown';
import { ErrorAlert } from '@/components/ErrorAlert';
import { ScheduledJobsTable } from '@/components/ScheduledJobsTable';
import { SessionStatusBadge } from '@/components/SessionStatusBadge';
import { TimeDisplay } from '@/components/TimeDisplay';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ActionButton } from '@/components/ui+/ActionButton';
import { ColorBadge } from '@/components/ui+/ColorBadge';
import { CopyTextButton } from '@/components/ui+/CopyTextButton';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { LogJson } from '@/components/ui+/LogJson';

const LinklySessionDetailsPage = () => {
  const { sessionUuid } = Route.useParams();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [jsonView, setJsonView] = useState<'parsed' | 'raw'>('parsed');
  const [deleteItem, setDeleteItem] = useState<{ id: string; type: 'request' | 'response' } | null>(
    null,
  );
  const convex = useConvex();

  // Get timeline of requests and responses
  const {
    data: timeline,
    error,
    isError,
    isPending: timelinePending,
  } = useQuery(api.linklyRequests.getSessionTimeline, { sessionUuid });

  // Get session to get linklyDeviceId for back button
  const { data: session, isPending: sessionPending } = useQuery(
    api.linklySessions.getBySessionUuid,
    { sessionUuid },
  );

  const toggleExpanded = (itemId: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleScheduleErrorRecoveryJob = async (timeInSeconds: number) => {
    // try {
    //   await convex.mutation(api.linkly.errorRecovery.scheduleJob, {
    //     sessionUuid,
    //     timeInSeconds,
    //   });
    // } catch (error) {
    //   toast.error('Error', { description: handleErrorOnClient(error) });
    // }
  };

  const handleConfirmDelete = async () => {
    if (!deleteItem) return;

    try {
      if (deleteItem.type === 'request') {
        await convex.mutation(api.linklyRequests.deleteRequest, {
          requestId: deleteItem.id,
        });
        toast.success('Request deleted successfully');
      } else {
        await convex.mutation(api.linklyResponses.deleteResponse, {
          responseId: deleteItem.id,
        });
        toast.success('Response deleted successfully');
      }
    } catch (error) {
      toast.error('Error', { description: getUserErrorMessage(error) });
    } finally {
      setDeleteItem(null);
    }
  };

  if (timelinePending || sessionPending) return <LoadingIcon />;
  if (isError) {
    return <ErrorAlert message={error.message} title="Error loading session details" />;
  }
  if (!timeline || timeline.length === 0) return <div>Session not found.</div>;

  const linklyDeviceId = session?.linklyDeviceId || null;

  return (
    <div>
      {linklyDeviceId && (
        <div className="mb-4">
          <ActionButton variant="outline" size="sm">
            <Link to="/a/linkly/devices/$id" params={{ id: linklyDeviceId }}>
              Back to Device
            </Link>
          </ActionButton>
        </div>
      )}

      <Card className={'mb-6'}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>
                Session Timeline
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({timeline.length} items)
                </span>
              </CardTitle>
              {session && (
                <div className="text-sm text-muted-foreground">
                  Created: <TimeDisplay timestamp={session._creationTime} showTime={false} />
                </div>
              )}
              <div className="text-sm text-muted-foreground">Session: {sessionUuid}</div>
              {/* Order Id */}
              {session?.orderId && (
                <div className="text-sm text-muted-foreground">
                  Order:{' '}
                  <Link
                    params={{ orderId: session.orderId }}
                    to="/a/orders/$orderId"
                    className="hover:underline"
                  >
                    {session.orderId}
                  </Link>
                </div>
              )}

              {session?.result && (
                <div className="mt-2">
                  <SessionStatusBadge result={session.result} />
                </div>
              )}
            </div>
            {session?.paymentUuid && (
              <ActionDropdown>
                <DropdownMenuItem onSelect={() => handleScheduleErrorRecoveryJob(0)}>
                  Schedule error recovery job now
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => handleScheduleErrorRecoveryJob(60)}>
                  Schedule error recovery job (60)
                </DropdownMenuItem>
              </ActionDropdown>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {timeline.map((sessionItem, index) => {
            const isExpanded = expandedItems.has(sessionItem.item._id);
            const isRequest = sessionItem.type === 'request';

            // Find the previous response for relative time calculation
            let previousResponseTime: number | null = null;
            if (!isRequest) {
              // Look for the previous response in the timeline
              for (let i = index - 1; i >= 0; i--) {
                if (timeline[i]?.type === 'response') {
                  previousResponseTime = timeline[i]?.item._creationTime ?? null;
                  break;
                }
              }
            }

            return (
              <Collapsible
                key={sessionItem.item._id}
                open={isExpanded}
                onOpenChange={() => toggleExpanded(sessionItem.item._id)}
              >
                <Card
                  className={`gap-0 py-0 ${
                    isRequest
                      ? 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30'
                      : 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/30'
                  }`}
                >
                  <CardHeader className="flex items-center p-0">
                    <div className="flex items-center justify-between w-full">
                      <CollapsibleTrigger className="flex flex-1 items-center justify-between gap-3 p-3 cursor-pointer hover:opacity-80 transition-opacity">
                        <div className="flex items-center gap-3">
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                          <ColorBadge color={isRequest ? 'blue' : 'green'}>
                            {isRequest ? 'Request' : 'Response'}
                          </ColorBadge>
                          {isRequest && 'requestType' in sessionItem.item && (
                            <ColorBadge color="gray">{sessionItem.item.requestType}</ColorBadge>
                          )}
                          {!isRequest && 'responseType' in sessionItem.item && (
                            <ColorBadge
                              color={
                                sessionItem.item.responseType === 'errorRecovery' ? 'pink' : 'gray'
                              }
                            >
                              {sessionItem.item.responseType}
                            </ColorBadge>
                          )}
                          {!isRequest &&
                            'parseError' in sessionItem.item &&
                            sessionItem.item.parseError && (
                              <ColorBadge color="red">Parse Error</ColorBadge>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                          {session && (
                            <div className="text-xs text-muted-foreground font-mono">
                              {`+${elapsedTime({
                                start: session._creationTime,
                                end: sessionItem.item._creationTime,
                              })}`}
                            </div>
                          )}
                          {!isRequest && previousResponseTime && (
                            <div className="text-xs text-muted-foreground/50 font-mono">
                              {`(+${elapsedTime(
                                {
                                  start: previousResponseTime,
                                  end: sessionItem.item._creationTime,
                                },
                                { showMs: false },
                              )})`}
                            </div>
                          )}
                        </div>
                      </CollapsibleTrigger>
                      <ActionDropdown
                        className="mr-3"
                        onDelete={() =>
                          setDeleteItem({
                            id: sessionItem.item._id,
                            type: isRequest ? 'request' : 'response',
                          })
                        }
                      />
                    </div>
                  </CardHeader>
                  <CollapsibleContent>
                    <CardContent className="px-3 pb-3 pt-0">
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell className="font-medium w-[150px]">ID</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm">{sessionItem.item._id}</span>
                                <CopyTextButton text={sessionItem.item._id} />
                              </div>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium">Created</TableCell>
                            <TableCell>
                              <TimeDisplay timestamp={sessionItem.item._creationTime} />
                            </TableCell>
                          </TableRow>
                          {isRequest && 'url' in sessionItem.item && (
                            <TableRow>
                              <TableCell className="font-medium">URL</TableCell>
                              <TableCell className="break-all text-sm">
                                {sessionItem.item.url}
                              </TableCell>
                            </TableRow>
                          )}
                          {isRequest && 'acceptedAt' in sessionItem.item && (
                            <TableRow>
                              <TableCell className="font-medium">Sent</TableCell>
                              <TableCell>
                                {sessionItem.item.acceptedAt ? (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger>
                                        <CheckCircle className="h-5 w-5 text-green-500" />
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p>
                                          Accepted at:{' '}
                                          <TimeDisplay
                                            timestamp={sessionItem.item.acceptedAt}
                                            primaryFormat="HH:mm:ss"
                                          />
                                        </p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                ) : (
                                  <span className="text-muted-foreground">Not sent</span>
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                          {isRequest &&
                            'runError' in sessionItem.item &&
                            sessionItem.item.runError && (
                              <TableRow>
                                <TableCell className="font-medium">Run Error</TableCell>
                                <TableCell className="text-red-500">
                                  {sessionItem.item.runError}
                                </TableCell>
                              </TableRow>
                            )}
                        </TableBody>
                      </Table>

                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="font-medium">
                            {isRequest ? 'Request Body:' : 'Response Data:'}
                          </div>
                          {!isRequest &&
                            'parsedData' in sessionItem.item &&
                            sessionItem.item.parsedData && (
                              <ToggleGroup
                                type="single"
                                size="sm"
                                value={jsonView}
                                onValueChange={(value) => {
                                  if (value) {
                                    setJsonView(value === 'raw' ? 'raw' : 'parsed');
                                  }
                                }}
                              >
                                <ToggleGroupItem
                                  className="px-3"
                                  value="raw"
                                  aria-label="Raw data"
                                  variant="outline"
                                >
                                  Raw
                                </ToggleGroupItem>
                                <ToggleGroupItem
                                  className="px-3"
                                  value="parsed"
                                  aria-label="Parsed data"
                                  variant="outline"
                                >
                                  Parsed
                                </ToggleGroupItem>
                              </ToggleGroup>
                            )}
                        </div>
                        <LogJson
                          jsonData={
                            isRequest && 'body' in sessionItem.item
                              ? sessionItem.item.body
                              : !isRequest &&
                                  'parsedData' in sessionItem.item &&
                                  sessionItem.item.parsedData &&
                                  jsonView === 'parsed'
                                ? sessionItem.item.parsedData
                                : 'rawData' in sessionItem.item
                                  ? sessionItem.item.rawData
                                  : {}
                          }
                          requireDebugParam={false}
                        />
                      </div>

                      {!isRequest &&
                        'parseError' in sessionItem.item &&
                        sessionItem.item.parseError && (
                          <div className="mt-4">
                            <div className="text-red-500 font-medium mb-1">Parse Error:</div>
                            <div className="text-red-500 bg-red-50 dark:bg-red-950/30 p-2 rounded text-sm">
                              {sessionItem.item.parseError}
                            </div>
                          </div>
                        )}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })}
        </CardContent>
      </Card>

      {/* Scheduled Jobs Table */}
      {session && (session.noResponseTimeoutJobId || session.sessionTimeoutJobId) && (
        <ScheduledJobsTable
          scheduledJobIds={[session.noResponseTimeoutJobId, session.sessionTimeoutJobId].filter(
            (id) => id !== null,
          )}
          referenceTime={session._creationTime}
          className="mb-6"
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deleteItem?.type === 'request' ? 'Request' : 'Response'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this{' '}
              {deleteItem?.type === 'request' ? 'request' : 'response'}? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const Route = createFileRoute('/a/linkly/sessions/$sessionUuid')({
  component: LinklySessionDetailsPage,
});
